import React, { useState } from 'react';
import { Box, Stack, Typography, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from "dayjs";
// ;

export type TimePeriod = {
  start: string;
  end: string;
};

export type WeeklySchedule = {
  [weekday: string]: TimePeriod[];
};

export type HolidaySchedule = {
  date: string; // YYYY-MM-DD
  periods: TimePeriod[];
};

export interface WeeklyWorkScheduleValue {
  weekly: WeeklySchedule;
  holidays: HolidaySchedule[];
}

interface WeeklyWorkScheduleProps {
  value?: WeeklyWorkScheduleValue;
  onChange?: (val: WeeklyWorkScheduleValue) => void;
  hideHolidays?: boolean;
  holidayMode?: 'single' | 'list';
}

// const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const weekdayKeys = ['1', '2', '3', '4', '5', '6', '0']; // dayjs().day() mapping
const defaultPeriod: TimePeriod = { start: '09:00', end: '18:00' };
const weekdayShort = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const WeeklyWorkSchedule: React.FC<WeeklyWorkScheduleProps> = ({ value, onChange, hideHolidays, holidayMode = 'list' }) => {
  const [weekly, setWeekly] = useState<WeeklySchedule>(() => {
    const initial: WeeklySchedule = {};
    weekdayKeys.forEach((k, _) => {
      initial[k] = value?.weekly?.[k] || [];
    });
    return initial;
  });
  const [holidays, setHolidays] = useState<HolidaySchedule[]>(value?.holidays || []);
  const [editDay, setEditDay] = useState<string | null>(null);
  const [editPeriods, setEditPeriods] = useState<TimePeriod[]>([]);

  // For single holiday mode
  const [singleHolidayPeriods, setSingleHolidayPeriods] = useState<TimePeriod[]>(() => {
    if (holidayMode === 'single' && value?.holidays?.[0]?.date === 'public_holiday') {
      return value.holidays[0].periods;
    }
    return [{ ...defaultPeriod }];
  });

  const emitChange = (w = weekly, h = holidays, singleH: TimePeriod[] = singleHolidayPeriods) => {
    setWeekly(w);
    setHolidays(h);
    setSingleHolidayPeriods(singleH);
    if (holidayMode === 'single') {
      onChange && onChange({ weekly: w, holidays: [{ date: 'public_holiday', periods: singleH }] });
    } else {
      onChange && onChange({ weekly: w, holidays: h });
    }
  };

  // Weekly handlers
  // const addPeriod = (day: string) => {
  //   const w = { ...weekly, [day]: [...(weekly[day] || []), { ...defaultPeriod }] };
  //   emitChange(w, holidays);
  // };
  // const removePeriod = (day: string, idx: number) => {
  //   const w = { ...weekly, [day]: (weekly[day] || []).filter((_, i) => i !== idx) };
  //   emitChange(w, holidays);
  // };
  // const updatePeriod = (day: string, idx: number, field: keyof TimePeriod, val: string) => {
  //   const w = { ...weekly, [day]: (weekly[day] || []).map((p, i) => i === idx ? { ...p, [field]: val } : p) };
  //   emitChange(w, holidays);
  // };

  // Open dialog to edit a day's periods
  const handleEditDay = (dayKey: string) => {
    setEditDay(dayKey);
    setEditPeriods(weekly[dayKey] && weekly[dayKey].length > 0 ? weekly[dayKey] : [{ ...defaultPeriod }]);
  };
  const handleSaveDay = () => {
    const w = { ...weekly, [editDay!]: editPeriods };
    emitChange(w, holidays);
    setEditDay(null);
  };

  // Holiday handlers
  const addHoliday = () => {
    const today = dayjs().format('YYYY-MM-DD');
    const h = [...holidays, { date: today, periods: [{ ...defaultPeriod }] }];
    emitChange(weekly, h);
  };
  const removeHoliday = (idx: number) => {
    const h = holidays.filter((_, i) => i !== idx);
    emitChange(weekly, h);
  };
  const updateHolidayDate = (idx: number, date: string) => {
    const h = holidays.map((hol, i) => i === idx ? { ...hol, date } : hol);
    emitChange(weekly, h);
  };
  const addHolidayPeriod = (idx: number) => {
    const h = holidays.map((hol, i) => i === idx ? { ...hol, periods: [...hol.periods, { ...defaultPeriod }] } : hol);
    emitChange(weekly, h);
  };
  const removeHolidayPeriod = (hidx: number, pidx: number) => {
    const h = holidays.map((hol, i) => i === hidx ? { ...hol, periods: hol.periods.filter((_, j) => j !== pidx) } : hol);
    emitChange(weekly, h);
  };
  const updateHolidayPeriod = (hidx: number, pidx: number, field: keyof TimePeriod, val: string) => {
    const h = holidays.map((hol, i) => i === hidx ? { ...hol, periods: hol.periods.map((p, j) => j === pidx ? { ...p, [field]: val } : p) } : hol);
    emitChange(weekly, h);
  };

  // For single holiday mode
  const addSingleHolidayPeriod = () => {
    const arr = [...singleHolidayPeriods, { ...defaultPeriod }];
    emitChange(weekly, holidays, arr);
  };
  const removeSingleHolidayPeriod = (idx: number) => {
    const arr = singleHolidayPeriods.filter((_, i) => i !== idx);
    emitChange(weekly, holidays, arr);
  };
  const updateSingleHolidayPeriod = (idx: number, field: keyof TimePeriod, val: string) => {
    const arr = singleHolidayPeriods.map((p, i) => i === idx ? { ...p, [field]: val } : p);
    emitChange(weekly, holidays, arr);
  };

  if (!hideHolidays && holidayMode === 'single') {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="subtitle2" mb={0.5} fontWeight={500}>Weekly Work Schedule</Typography>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, width: '100%' }}>
          {weekdayKeys.map((k, i) => {
            const periods = weekly[k] || [];
            const label = periods.length === 0 ? '-' : periods.map(p => `${p.start}-${p.end}`).join(', ');
            // Highlight Sat (6) and Sun (0) with soft yellow
            const isWeekend = k === '6' || k === '0';
            return (
              <Box
                key={k}
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: 1,
                  px: 1.2,
                  py: 0.7,
                  minWidth: 0,
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isWeekend ? '#fffbe6' : '#fafbfc',
                  '&:hover': { background: isWeekend ? '#fff7cc' : '#f0f4fa' },
                  flex: 1,
                  flexGrow: 1,
                  flexBasis: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onClick={() => handleEditDay(k)}
              >
                <Typography variant="caption" fontWeight={600}>{weekdayShort[i]}</Typography>
                <Typography variant="caption" display="block" sx={{ fontSize: 12, mt: 0.2 }}>{label}</Typography>
              </Box>
            );
          })}
        </Stack>
        <Dialog open={!!editDay} onClose={() => setEditDay(null)} maxWidth="xs" fullWidth>
          <DialogTitle>Edit {editDay ? weekdayShort[weekdayKeys.indexOf(editDay)] : ''}</DialogTitle>
          <DialogContent>
            <Stack spacing={1} mt={1}>
              {editPeriods.map((period, idx) => (
                <Stack direction="row" spacing={1} alignItems="center" key={idx}>
                  <TextField
                    label="Start"
                    type="time"
                    size="small"
                    value={period.start}
                    onChange={e => setEditPeriods(p => p.map((per, i) => i === idx ? { ...per, start: e.target.value } : per))}
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label="End"
                    type="time"
                    size="small"
                    value={period.end}
                    onChange={e => setEditPeriods(p => p.map((per, i) => i === idx ? { ...per, end: e.target.value } : per))}
                    sx={{ width: 100 }}
                  />
                  <IconButton size="small" onClick={() => setEditPeriods(p => p.length === 1 ? p : p.filter((_, i) => i !== idx))}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button onClick={() => setEditPeriods(p => [...p, { ...defaultPeriod }])} startIcon={<AddIcon />} size="small">
                Add Period
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDay(null)}>Cancel</Button>
            <Button onClick={handleSaveDay} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>
        <Box sx={{ mt: 1, background: '#e6f9e6', borderRadius: 1, p: 1 }}>
          <Typography variant="subtitle2" fontWeight={500}>Holiday</Typography>
          <Stack spacing={1} direction="row" alignItems="center">
            {singleHolidayPeriods.map((period, idx) => (
              <React.Fragment key={idx}>
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  value={period.start}
                  onChange={e => updateSingleHolidayPeriod(idx, 'start', e.target.value)}
                  sx={{ width: 100, background: '#e6f9e6' }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  value={period.end}
                  onChange={e => updateSingleHolidayPeriod(idx, 'end', e.target.value)}
                  sx={{ width: 100, background: '#e6f9e6' }}
                />
                <IconButton size="small" onClick={() => removeSingleHolidayPeriod(idx)} disabled={singleHolidayPeriods.length === 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </React.Fragment>
            ))}
            <Button onClick={addSingleHolidayPeriod} startIcon={<AddIcon />} size="small">
              Add Period
            </Button>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" mb={0.5} fontWeight={500}>Weekly Work Schedule</Typography>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, width: '100%' }}>
        {weekdayKeys.map((k, i) => {
          const periods = weekly[k] || [];
          const label = periods.length === 0 ? '-' : periods.map(p => `${p.start}-${p.end}`).join(', ');
          // Highlight Sat (6) and Sun (0) with soft yellow
          const isWeekend = k === '6' || k === '0';
          return (
            <Box
              key={k}
              sx={{
                border: '1px solid #ccc',
                borderRadius: 1,
                px: 1.2,
                py: 0.7,
                minWidth: 0,
                textAlign: 'center',
                cursor: 'pointer',
                background: isWeekend ? '#fffbe6' : '#fafbfc',
                '&:hover': { background: isWeekend ? '#fff7cc' : '#f0f4fa' },
                flex: 1,
                flexGrow: 1,
                flexBasis: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              onClick={() => handleEditDay(k)}
            >
              <Typography variant="caption" fontWeight={600}>{weekdayShort[i]}</Typography>
              <Typography variant="caption" display="block" sx={{ fontSize: 12, mt: 0.2 }}>{label}</Typography>
            </Box>
          );
        })}
      </Stack>
      <Dialog open={!!editDay} onClose={() => setEditDay(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit {editDay ? weekdayShort[weekdayKeys.indexOf(editDay)] : ''}</DialogTitle>
        <DialogContent>
          <Stack spacing={1} mt={1}>
            {editPeriods.map((period, idx) => (
              <Stack direction="row" spacing={1} alignItems="center" key={idx}>
                <TextField
                  label="Start"
                  type="time"
                  size="small"
                  value={period.start}
                  onChange={e => setEditPeriods(p => p.map((per, i) => i === idx ? { ...per, start: e.target.value } : per))}
                  sx={{ width: 100 }}
                />
                <TextField
                  label="End"
                  type="time"
                  size="small"
                  value={period.end}
                  onChange={e => setEditPeriods(p => p.map((per, i) => i === idx ? { ...per, end: e.target.value } : per))}
                  sx={{ width: 100 }}
                />
                <IconButton size="small" onClick={() => setEditPeriods(p => p.length === 1 ? p : p.filter((_, i) => i !== idx))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))}
            <Button onClick={() => setEditPeriods(p => [...p, { ...defaultPeriod }])} startIcon={<AddIcon />} size="small">
              Add Period
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDay(null)}>Cancel</Button>
          <Button onClick={handleSaveDay} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
      {!hideHolidays && holidayMode === 'list' && <>
        <Typography variant="subtitle2" mt={1} mb={0.5} fontWeight={500}>Public Holidays</Typography>
        <Stack spacing={0.5}>
          {holidays.map((hol, hidx) => (
            <Stack key={hidx} direction="row" alignItems="center" spacing={0.5} sx={{ border: '1px solid #eee', borderRadius: 1, p: 0.5 }}>
              <TextField
                label=""
                type="date"
                size="small"
                value={hol.date}
                onChange={e => updateHolidayDate(hidx, e.target.value)}
                sx={{ width: 120 }}
                inputProps={{ style: { padding: 4, fontSize: 13 } }}
              />
              {hol.periods.map((period, pidx) => (
                <Stack direction="row" spacing={0.5} alignItems="center" key={pidx}>
                  <TextField
                    label=""
                    type="time"
                    size="small"
                    value={period.start}
                    onChange={e => updateHolidayPeriod(hidx, pidx, 'start', e.target.value)}
                    sx={{ width: 70 }}
                    inputProps={{ style: { padding: 4, fontSize: 13 } }}
                  />
                  <Typography variant="body2" sx={{ mx: 0.2 }}>-</Typography>
                  <TextField
                    label=""
                    type="time"
                    size="small"
                    value={period.end}
                    onChange={e => updateHolidayPeriod(hidx, pidx, 'end', e.target.value)}
                    sx={{ width: 70 }}
                    inputProps={{ style: { padding: 4, fontSize: 13 } }}
                  />
                  <IconButton size="small" onClick={() => removeHolidayPeriod(hidx, pidx)} disabled={hol.periods.length === 1} sx={{ p: 0.5 }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <IconButton onClick={() => addHolidayPeriod(hidx)} size="small" sx={{ p: 0.5 }}>
                <AddIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={() => removeHoliday(hidx)} sx={{ p: 0.5 }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
          <IconButton onClick={addHoliday} size="small" sx={{ p: 0.5, alignSelf: 'flex-start' }}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Stack>
      </>}
    </Box>
  );
};

export default WeeklyWorkSchedule; 